import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';

// Async thunks for API calls
export const fetchAllSpots = createAsyncThunk(
  'spots/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Fetches only one page's worth of spots (search + pagination handled server-side)
// so the My Spots page never has to download the user's entire spot list at once.
export const fetchPaginatedSpots = createAsyncThunk(
  'spots/fetchPaginated',
  async ({ search = '', page = 1, perPage = 9 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      if (search) {
        params.set('search', search);
      }

      const response = await apiFetch(`${API_BASE_URL}/spots/paginated?${params.toString()}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchSpot = createAsyncThunk(
  'spots/getDetails',
  async (spotId, { rejectWithValue, getState }) => {
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const createSpot = createAsyncThunk(
  'spots/create',
  async (spotData, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/create`, {
        method: 'POST',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateSpot = createAsyncThunk(
  'spots/update',
  async ({ spotId, spotData }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/update`, {
        method: 'PUT',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const deleteSpot = createAsyncThunk(
  'spots/delete',
  async (spotId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/delete`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        return rejectWithValue(data);
      }

      return spotId; // Return the deleted spot ID
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const reorderSpots = createAsyncThunk(
  'spots/reorder',
  async (spotIds, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ spot_ids: spotIds }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return spotIds;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const savedSpotsSlice = createSlice({
  name: 'savedSpots',
  initialState: {
    spots: {},
    spotOrder: [],
    paginatedIds: [],
    pagination: { currentPage: 1, lastPage: 1, total: 0, perPage: 9 },
    mySpotsQuery: { page: 1, search: '' },
    isLoadingPaginated: false,
    currentSpot: null,
    lastSpotRoute: null,
    isLoading: false,
    isDeleting: false,
    errors: null,
    isUpdating: false,
    isSaving: false,
  },
  reducers: {
    clearErrors: (state) => {
      state.errors = null;
    },
    clearCurrentSpot: (state) => {
      state.currentSpot = null;
    },
    setCurrentSpot: (state, action) => {
      state.currentSpot = action.payload;
    },
    setLastSpotRoute: (state, action) => {
      state.lastSpotRoute = action.payload;
    },
    clearLastSpotRoute: (state) => {
      state.lastSpotRoute = null;
    },
    setSpotOrder: (state, action) => {
      state.spotOrder = action.payload;
    },
    setMySpotsQuery: (state, action) => {
      state.mySpotsQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Spots
      .addCase(fetchAllSpots.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchAllSpots.fulfilled, (state, action) => {
        state.isLoading = false;
        // Transform array to object with id as key
        state.spots = action.payload.reduce((acc, spot) => {
          acc[spot.id] = spot;
          return acc;
        }, {});
        // Backend already returns spots ordered by sort_order; keep that display order separately
        // since object keys with numeric-looking ids get silently re-sorted ascending by JS.
        state.spotOrder = action.payload.map((spot) => spot.id);
        state.errors = null;
      })
      .addCase(fetchAllSpots.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spots'];
      })
      // Fetch Spot by ID
      .addCase(fetchSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.spots[action.payload.id] = action.payload;
        state.currentSpot = action.payload;
        state.errors = null;
      })
      .addCase(fetchSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spot details'];
      })
      // Create Spot
      .addCase(createSpot.pending, (state) => {
        state.errors = null;
        state.isSaving = true;
      })
      .addCase(createSpot.fulfilled, (state, action) => {
        state.spots[action.payload.id] = action.payload;
        state.spotOrder.push(action.payload.id);
        state.currentSpot = action.payload;
        state.errors = null;
        state.isSaving = false;
      })
      .addCase(createSpot.rejected, (state, action) => {
        state.errors = action.payload?.errors || ['Failed to create spot'];
        state.isSaving = false;
      })
      // Update Spot
      .addCase(updateSpot.pending, (state) => {
        state.errors = null;
        state.isUpdating = true;
      })
      .addCase(updateSpot.fulfilled, (state, action) => {
        const updatedSpot = action.payload;
        state.spots[updatedSpot.id] = updatedSpot;
        state.currentSpot = updatedSpot;
        state.errors = null;
        state.isUpdating = false;
      })
      .addCase(updateSpot.rejected, (state, action) => {
        state.errors = action.payload?.errors || ['Failed to update spot'];
        state.isUpdating = false;
      })
      // Delete Spot
      .addCase(deleteSpot.pending, (state) => {
        state.isDeleting = true;
        state.errors = null;
      })
      .addCase(deleteSpot.fulfilled, (state, action) => {
        state.isDeleting = false;
        const spotId = action.payload; // payload is just the spotId
        delete state.spots[spotId];
        state.spotOrder = state.spotOrder.filter((id) => String(id) !== String(spotId));
        // Clear current spot if it was the deleted one
        if (state.currentSpot?.id === spotId) {
          state.currentSpot = null;
        }
        // Don't leave the My Spots tab pointing at a deleted spot's page
        if (String(state.lastSpotRoute?.spotId) === String(spotId)) {
          state.lastSpotRoute = null;
        }
        state.errors = null;
      })
      .addCase(deleteSpot.rejected, (state, action) => {
        state.isDeleting = false;
        state.errors = action.payload?.errors || ['Failed to delete spot'];
      })
      // Reorder Spots
      .addCase(reorderSpots.fulfilled, (state, action) => {
        state.spotOrder = action.payload;
      })
      .addCase(reorderSpots.rejected, (state, action) => {
        state.errors = action.payload?.errors || ['Failed to save spot order'];
      })
      // Fetch Paginated Spots
      .addCase(fetchPaginatedSpots.pending, (state) => {
        state.isLoadingPaginated = true;
        state.errors = null;
      })
      .addCase(fetchPaginatedSpots.fulfilled, (state, action) => {
        state.isLoadingPaginated = false;
        const { data, current_page, last_page, total, per_page } = action.payload;
        data.forEach((spot) => {
          state.spots[spot.id] = spot;
        });
        state.paginatedIds = data.map((spot) => spot.id);
        state.pagination = { currentPage: current_page, lastPage: last_page, total, perPage: per_page };
      })
      .addCase(fetchPaginatedSpots.rejected, (state, action) => {
        state.isLoadingPaginated = false;
        state.errors = action.payload?.errors || ['Failed to fetch spots'];
      });
  },
});

export const { clearError, clearCurrentSpot, setCurrentSpot, setLastSpotRoute, clearLastSpotRoute, setSpotOrder, setMySpotsQuery } = savedSpotsSlice.actions;
export default savedSpotsSlice.reducer;